import {useEffect, useState} from 'react';
import axios from 'axios';

import {config} from '../config.js';
import {parse_cookies} from './cookie-management.js';
import {clearCookies} from '../react-utils/src/helperfunctions.js';
import { ACCESS_TOKEN, UNION_NUMBER } from '../constants/cookie-constants';
import { error } from './feedback';

export class API {
	static _merge_cfg(cfg_delta){
		//start with the configuration defined in the config file
		let cfg=config.axios;

		//add in session (authorization) headers as needed
		const cookies=parse_cookies();
		if(cookies.hasOwnProperty(ACCESS_TOKEN)){
			cfg['headers']['Authorization']=cookies[ACCESS_TOKEN];
		}

		//and finally set anything explicitly defined by the user
		const keys=Object.keys(cfg_delta);
		for(let key_idx=0;key_idx<keys.length;key_idx++){
			cfg[keys[key_idx]]=cfg_delta[keys[key_idx]];
		}

		//and return the newly merged configuration
		return cfg;
	}

	static default_error_handler(err){
		if (!err) {
			return;
		}
		let cookies = parse_cookies();
		let union_number = cookies[UNION_NUMBER];
		if(('response' in err) && (err.response!==undefined) && (err.response!==null)){
			if(err.response.status===401){
				clearCookies();
				window.location.href='/login';
				window.DD_LOGS.logger.error('Default Error Handler: Unauthorized',
				{ response: err.response, status: 401});
			} else if (err.response.data && err.response.data.message ) {
				error(`Request failed: ${err.response.data.message}`);
				window.DD_LOGS.logger.error(`Default Error Handler: Request failed: ${err.response.data.message}`,
				{ union_number: union_number, status: err.response.status, data: err.response.data, message:err.response.message});
			}
			else if(err.response.data && err.response.data.detail){
				error(`Request failed: ${err.response.data.detail}`);
				window.DD_LOGS.logger.error(`Default Error Handler: Request failed: ${err.response.data.detail}`,
				{ union_number: union_number,status: err.response.status, data: err.response.data, detail:err.response.detail});
			}else {
				error('Sorry we are unable to process your request.');
				window.DD_LOGS.logger.error('Default Error Handler: Cannot process request.',
				{ union_number: union_number,status: err.response.status, response: err.response});
			}
		} else {
			error('Sorry we are having trouble connecting to the servers.');
			window.DD_LOGS.logger.error('Default Error Handler: (No response, likely a front-end error).',
			{ union_number: union_number, error: err});
		}
	}

	static request(path,cfg_delta){
		cfg_delta['url']=path;
		return axios.request(API._merge_cfg(cfg_delta));
	}

	static get(path,cfg_delta={}, prepend_workspace=true){
		cfg_delta['method']='get';
		path = prependWorkspaceId(path,prepend_workspace);
		return API.request(path,cfg_delta);
	}
	static put(path,cfg_delta={}, prepend_workspace=true){
		cfg_delta['method']='put';
		path = prependWorkspaceId(path,prepend_workspace);
		return API.request(path,cfg_delta);
	}
	static post(path,cfg_delta={}, prepend_workspace=true){
		cfg_delta['method']='post';
		path = prependWorkspaceId(path,prepend_workspace);
		return API.request(path,cfg_delta);
	}
	static delete(path,cfg_delta={}, prepend_workspace=true){
		cfg_delta['method']='delete';
		path = prependWorkspaceId(path,prepend_workspace);
		return API.request(path,cfg_delta);
	}
}

function prependWorkspaceId(path,prepend_workspace=true){
	if(prepend_workspace){
		let workspace_id = config.workspace_id;
		if(workspace_id){
			if(!path.startsWith('/')){
				path=`/${path}`;
			}
			path = `/workspace/${workspace_id}${path}`;
		}
	}
	return path;
}

export const usePostData = (url, dataToPost) => {
	let errorResponse = '';
	let data = {};
	let isLoading = true;
	const postData = async () => {
		try {
			const result = await API.post(url, {'data': dataToPost});
			data = result;
		} catch (error) {
			errorResponse = error;
			API.default_error_handler(error);
		}
		isLoading = false;
	};
	postData();
	return [{data, isLoading, errorResponse}];
};

export const usePutData = (url, dataToPut) => {
	let errorResponse = '';
	let data = {};
	let isLoading = true;
	const putData = async () => {
		try {
			const result = await API.put(url, { 'data': dataToPut });
			data = result;
		} catch (error) {
			errorResponse = error;
			API.default_error_handler(error);
		}
		isLoading = false;
	};
	putData();
	return [{data, isLoading, errorResponse}];
};

// Custom hook that will get data from url.
// Hook will only be called once when component mounts.
// To make fire again, just call again, with a new url.
// data: data returned by server if successful
// url: endpoint of get request
// isLoading: is request in progress.
// error: error if request is not successful
export const useGetData = (initialUrl, initialData = []) => {
	const [data, setData] = useState(initialData);
	const [url, setUrl] = useState(initialUrl);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState();
	useEffect(() => {
		const getData = async () => {
			setError();
			setIsLoading(true);
			try {
				const result = await API.get(url);
				setData(result.data);
			} catch (error) {
				setError(error);
				API.default_error_handler(error);
			}
			setIsLoading(false);
		};
		getData();
	}, [url]);
	return [{ data, isLoading, error }, setUrl];
};
